import {FC, useState, useCallback, useMemo, useEffect} from 'react';
import {useParams} from 'wouter';

import {Door} from '@shared/models/api_models';

import {Pagination} from '@shared_web/components/core/pagination';

import {AlertSymbol} from '@mavismart_frontend/components/alert_symbol';
import {TableWrapper} from '@mavismart_frontend/components/common_styles';
import {InputSearch} from '@mavismart_frontend/components/input_search';
import {Loader} from '@mavismart_frontend/components/loader';
import {
  itemsCountLabel,
  itemsPerPageLabel,
  paginate,
  PAGINATION_MAX_LINES,
} from '@mavismart_frontend/components/pagination_utils';
import {StickySearchWrapper} from '@mavismart_frontend/components/sticky_search_wrapper';
import {Translate} from '@mavismart_frontend/components/translate';
import {GenericCell} from '@mavismart_frontend/pages/cells/generic_cell';
import {getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';
import {useAllDoors} from '@mavismart_frontend/stores/all_doors_store';

interface DoorsListProps {
  doorIds?: string[];
}

type PartialDoor = Partial<Door> & {id: string};

export const DoorsList: FC<DoorsListProps> = ({doorIds}) => {
  const {groupId, doorId: doorIdParam} = useParams<{doorId?: string; groupId?: string}>();
  const account = useAccount();
  const locale = getUserLocale(account);

  // FETCHING
  const allDoors = useAllDoors();
  const doors = useMemo(
    () =>
      doorIds === undefined
        ? undefined
        : doorIds.map(id => {
            const door: PartialDoor = allDoors?.doors.get(id) ?? {id};
            return door;
          }),
    [allDoors, doorIds]
  );

  // FILTERING
  const [filteredDoors, setFilteredDoors] = useState<PartialDoor[] | undefined>(doors);
  useEffect(() => setFilteredDoors(doors), [doors]);

  // SORTING
  const sortedDoors = useMemo(() => {
    if (!filteredDoors) return undefined;
    return [...filteredDoors].sort((a, b) => {
      // INFO: sort by id desc because the date is implemented in the PORXXXXXX
      return b.id.localeCompare(a.id);
    });
  }, [filteredDoors]);

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const paginatedDoors = useMemo(
    () => paginate(sortedDoors, currentPage),
    [sortedDoors, currentPage]
  );

  const getLabel = useCallback(
    (door: PartialDoor) => `${door.label ?? ''} ${door.id} ${door.setupDate ?? ''}`,
    []
  );
  const basePath = useMemo(() => {
    if (groupId) {
      return `/groups/${groupId}`;
    }
    return '';
  }, [groupId]);

  if (paginatedDoors === undefined || sortedDoors === undefined) {
    return <Loader />;
  }

  return (
    <>
      <StickySearchWrapper>
        <InputSearch items={doors} setFilteredItems={setFilteredDoors} getLabel={getLabel} />
      </StickySearchWrapper>
      {paginatedDoors?.length === 0 ? (
        <Translate value="no_door_found" />
      ) : (
        <TableWrapper>
          {paginatedDoors.map(d => {
            const doorId = d.id;
            const door = allDoors?.doors.get(doorId);
            const isHighlighted = doorId === doorIdParam;
            return (
              <GenericCell
                key={doorId}
                id={doorId}
                title={doorId}
                subtitle={door?.label ?? door?.setupDate ?? '...'}
                icon={
                  door ? (
                    <AlertSymbol level={door.status ?? 'green'} />
                  ) : (
                    <AlertSymbol level="grey" />
                  )
                }
                to={`${basePath}/doors/${doorId}`}
                highlighted={isHighlighted}
              />
            );
          })}
        </TableWrapper>
      )}
      <Pagination
        totalItems={sortedDoors?.length ?? 0}
        limit={PAGINATION_MAX_LINES}
        onPageChange={setCurrentPage}
        itemsCountLabel={itemsCountLabel}
        itemsPerPageLabel={itemsPerPageLabel}
        locale={locale}
        singlePageBehavior="hide"
      />
    </>
  );
};
